import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PrequalificationModel } from 'src/app/models/prequalification.model';
import { PrequalificationService } from 'src/app/prequalifications/prequalification.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { AppService } from 'src/app/app.service';
import { RBACService } from '../../../shared/rbac.service';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-contractors-prequalifications',
  templateUrl: './contractors-prequalifications.component.html',
  styleUrls: ['./contractors-prequalifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractorsPrequalificationsComponent implements OnInit {
  prequal = new PrequalificationModel();
  contractorId: number;
  score: number;
  noPrequalExists: boolean = false;
  prequalExistsNoView: boolean = false;

  // RBAC permission flags. These are resolved once in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public prequalService: PrequalificationService,
    private router: Router,
    public utils: UtilsService,
    private api: ApiRequestService,
    public app: AppService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve the RBAC permission flags first so they are always set, even when we return early.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Check RBAC permission: user needs at least one pre-qualifications permission
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_VIEW
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_EDIT
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access pre-qualifications.'
      );
      this.router.navigate(['/contractors']);
      return;
    }

    this.prequal.id = Number(this.route.snapshot.params['prequal_id']);
    this.contractorId = Number(this.route.snapshot.parent.params.contractor_id);

    // Only fetch existing pre-qualification if user has view permission.
    // Create-only users check existence via lightweight endpoint.
    if (
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      // Check existence first to avoid 404 error toast when no pre-qualification exists
      this.api
        .makeRequest(
          'get',
          `v2/contractor-pre-qualifications/${this.contractorId}/exists`
        )
        .then((response: { exists: boolean }) => {
          if (response.exists) {
            this.getPreQualification();
          } else if (
            this.rbac.userCanAccess(
              [
                RBACService.getPermission(
                  RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
                  RBACService.RBAC_A.A_CREATE
                )
              ],
              true
            )
          ) {
            this.router.navigate(['new/edit'], { relativeTo: this.route });
          } else {
            this.noPrequalExists = true;
          }
        });
    } else {
      // User without view: check if a pre-qualification already exists
      this.api
        .makeRequest(
          'get',
          `v2/contractor-pre-qualifications/${this.contractorId}/exists`
        )
        .then((response: { exists: boolean }) => {
          if (response.exists) {
            this.prequalExistsNoView = true;
          } else {
            this.router.navigate(['new/edit'], { relativeTo: this.route });
          }
        });
    }
  }

  getPreQualification() {
    return this.api
      .makeRequest(
        'get',
        `v2/contractor-pre-qualifications/${this.contractorId}`
      )
      .then((response) => {
        this.prequal = response;
      })
      .catch((errorResponse) => {
        // No pre-qualification exists: redirect to create form if user has create permission,
        // otherwise show an inline warning on this page.
        if (
          this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
                RBACService.RBAC_A.A_CREATE
              )
            ],
            true
          )
        ) {
          this.router.navigate(['new/edit'], { relativeTo: this.route });
        } else {
          this.noPrequalExists = true;
        }
      });
  }

  onDelete() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete pre-qualifications.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Pre-Qualification',
      `Are you sure you want to remove the "${
        this.prequal.name
      }" Pre-Qualification?`,
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/contractor-pre-qualifications/${this.contractorId}`
          )
          .then(() => {
            this.utils.showToast(
              'The Pre-Qualification was successfully removed.'
            );
            this.router.navigate([
              '/contractors',
              this.contractorId,
              'prequalifications',
              'new',
              'edit'
            ]);
          })
          .catch(() => {});
      }
    );
  }

  // setPrequal() {
  //   this.prequalService
  //     .findForContractor(this.contractorId)
  //     .subscribe((response) => {
  //       if (!response.data) {
  //         this.router.navigate(['new/edit'], { relativeTo: this.route });
  //       }
  //       this.prequal = response.data;
  //     });
  // }

  // delete() {
  //   this.prequalService
  //     .remove([this.prequal.id])
  //     .subscribe((prequal: PrequalificationModel) => {
  //       this.utils.showToast(`Deleted: "${prequal.name}".`);
  //       this.router.navigate([
  //         'prequalifications',
  //         {
  //           contractorId: this.prequal.contractor.id
  //         }
  //       ]);
  //     });
  // }

  async onDownloadPDF() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view pre-qualifications.'
      );
      return;
    }

    const data = await this.prequalService.exportPdf(this.prequal.id);
    saveAs(data, `Prequalification ${this.prequal.name}`);
  }

  getExpiryClass(expiresAt: number): string {
    const now = Date.now();
    const expiryDate = expiresAt * 1000;
    const daysUntilExpiry = (expiryDate - now) / (1000 * 60 * 60 * 24);

    if (daysUntilExpiry < 0) {
      return 'expiry-expired'; // Red
    }

    if (daysUntilExpiry <= 7) {
      return 'expiry-warning'; // Orange
    }

    return 'expiry-valid'; // Green
  }

  protected readonly RBACService = RBACService;
}
